import { useState } from "react"
import { useNavigate } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import { showUndoToast } from "@/lib/undoToast"
import { getCurrentUser } from "../../lib/auth"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import { StatusPill } from "@/components/cccd/StatusPill"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { PencilIcon, TrashBinIcon } from "../../icons"
import { toCsv, downloadCsv } from "../../lib/csv"
import {
  ACADEMIC_INSTITUTION_CSV_TEMPLATE_HEADERS,
  ACADEMIC_INSTITUTION_CSV_TEMPLATE_EXAMPLE_ROW,
} from "../../lib/academicInstitutionsCsvImport"

// ── Types ──────────────────────────────────────────────────────────────────

type Sector = "public" | "private"

type AcademicInstitution = {
  id: string
  name: string
  slug: string
  sector?: Sector | null
  city?: string | null
  country?: string | null
  cccd_teams_count?: number
  tra_members_count?: number
  createdAt?: string
}

const SECTOR_LABELS: Record<Sector, string> = { public: "Pública", private: "Privada" }
const SECTOR_TONE: Record<Sector, "brand" | "gray"> = { public: "brand", private: "gray" }

// ── Component ──────────────────────────────────────────────────────────────

export default function AcademicInstitutionsPage() {
  const navigate = useNavigate()
  const isSuperAdmin = getCurrentUser()?.role === "admin"
  const [search, setSearch] = useState("")
  const [sectorFilter, setSectorFilter] = useState<"" | Sector>("")
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false)

  const {
    rows: allInstitutions,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    reload,
  } = useServerTable<AcademicInstitution>({
    endpoint: "/academic-institutions/admin",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    params: { search, sector: sectorFilter || undefined },
    extractRows: (data) => data.institutions ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })
  const institutions = allInstitutions.filter((i) => !hiddenIds.has(i.id))

  const withReferences = institutions.filter(
    (i) => (i.cccd_teams_count ?? 0) > 0 || (i.tra_members_count ?? 0) > 0
  ).length

  function onDownloadTemplate() {
    const csv = toCsv([ACADEMIC_INSTITUTION_CSV_TEMPLATE_HEADERS, ACADEMIC_INSTITUTION_CSV_TEMPLATE_EXAMPLE_ROW])
    downloadCsv("instituciones-educativas-plantilla.csv", csv)
  }

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAllVisible() {
    setSelectedIds(new Set(institutions.map((i) => i.id)))
  }

  function clearSelection() {
    setSelectedIds(new Set())
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return
    setBulkDeleting(true)
    try {
      const res = await api.post("/academic-institutions/admin/bulk-delete", { ids: Array.from(selectedIds) })
      const result = res.data.data as { deleted: string[]; skipped: { id: string; reason: string }[] }
      if (result.deleted.length > 0) {
        toastSuccess(`${result.deleted.length} institución${result.deleted.length === 1 ? "" : "es"} eliminada${result.deleted.length === 1 ? "" : "s"}.`)
      }
      if (result.skipped.length > 0) {
        toastError(`${result.skipped.length} no se eliminaron (tienen registros asociados u otra razón).`)
      }
      clearSelection()
      await reload()
    } catch (err) {
      toastError(err)
    } finally {
      setBulkDeleting(false)
      setConfirmingBulkDelete(false)
    }
  }

  // Borrado optimista con deshacer, en vez del confirm() nativo — mismo
  // patrón que EventCategoriesSection/EventPlansSection/AddonsCatalogPage
  // (cccd-ux-redesign, cccd-admin-design-system).
  function onDelete(inst: AcademicInstitution) {
    setHiddenIds((prev) => new Set(prev).add(inst.id))
    showUndoToast({
      message: `"${inst.name}" eliminado`,
      onUndo: () => setHiddenIds((prev) => { const next = new Set(prev); next.delete(inst.id); return next }),
      onCommit: async () => {
        try {
          await api.delete(`/academic-institutions/admin/${inst.id}`)
          await reload()
        } catch (err) {
          setHiddenIds((prev) => { const next = new Set(prev); next.delete(inst.id); return next })
          toastError(err)
        }
      },
    })
  }

  const columns: DataTableColumn<AcademicInstitution>[] = [
    ...(isSuperAdmin
      ? [
          {
            key: "select",
            label: "",
            render: (inst: AcademicInstitution) => (
              <input
                type="checkbox"
                checked={selectedIds.has(inst.id)}
                onChange={() => toggleSelected(inst.id)}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
              />
            ),
          } satisfies DataTableColumn<AcademicInstitution>,
        ]
      : []),
    {
      key: "name",
      label: "Institución",
      sortable: true,
      render: (inst) => (
        <>
          <p className="font-semibold text-gray-900 dark:text-white">{inst.name}</p>
          {(inst.city || inst.country) && (
            <p className="text-xs text-gray-500 mt-0.5">{[inst.city, inst.country].filter(Boolean).join(", ")}</p>
          )}
        </>
      ),
    },
    {
      key: "sector",
      label: "Sector",
      align: "center",
      render: (inst) =>
        inst.sector ? (
          <StatusPill tone={SECTOR_TONE[inst.sector]} label={SECTOR_LABELS[inst.sector]} />
        ) : (
          <span className="text-xs text-gray-500 dark:text-gray-400">Sin clasificar</span>
        ),
    },
    {
      key: "cccd_teams_count",
      label: "Equipos CCCD",
      align: "center",
      render: (inst) => (
        <span className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          {inst.cccd_teams_count ?? 0}
        </span>
      ),
    },
    {
      key: "tra_members_count",
      label: "Miembros TRA",
      align: "center",
      render: (inst) => (
        <span className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          {inst.tra_members_count ?? 0}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Acciones",
      align: "right",
      render: (inst) => (
        <RowActionsMenu
          actions={[
            {
              label: "Editar",
              icon: <PencilIcon />,
              to: `/cccd/academic-institutions/${inst.id}/edit`,
              requires: "cccd:academic-institutions:edit",
            },
            {
              label: "Eliminar",
              icon: <TrashBinIcon />,
              onClick: () => onDelete(inst),
              variant: "danger",
              requires: "cccd:academic-institutions:edit",
            },
          ]}
        />
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, { label: "CCCD", to: "/cccd" }, "Instituciones educativas"]}
      title="Instituciones educativas"
      subtitle="De dónde vienen los integrantes de un equipo CCCD o las personas certificadas Tripoli — distinto de las instituciones-cliente que contratan semilleros"
      tabs={[{ label: "Instituciones educativas", to: "/cccd/academic-institutions", active: true }]}
      metrics={[
        { label: "Total", value: String(total) },
        {
          label: "Con referencias (esta página)",
          value: String(withReferences),
          tone: "good",
          hint: "Ya asignadas a un equipo o miembro — solo cuenta las filas visibles, no el total",
        },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar institución educativa...",
        },
        filters: [
          {
            type: "select",
            key: "sector",
            label: "Sector",
            value: sectorFilter,
            onChange: (value) => setSectorFilter(value as "" | Sector),
            options: [
              { label: "Todos los sectores", value: "" },
              { label: "Pública", value: "public" },
              { label: "Privada", value: "private" },
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,
        onCreate: () => navigate("/cccd/academic-institutions/new"),
        createLabel: "+ Nueva institución educativa",
        onImport: () => navigate("/cccd/academic-institutions/import"),
        requires: "cccd:academic-institutions:edit",
        extra: (
          <button
            type="button"
            onClick={onDownloadTemplate}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.04]"
          >
            Descargar plantilla CSV
          </button>
        ),
      }}
    >
      {isSuperAdmin && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm dark:border-gray-800 dark:bg-white/[0.02]">
          <span role="status" aria-live="polite" className="text-gray-500 dark:text-gray-400">
            {bulkDeleting
              ? "Eliminando instituciones..."
              : selectedIds.size > 0
                ? `${selectedIds.size} seleccionada${selectedIds.size === 1 ? "" : "s"}`
                : "Borrado masivo (solo Administrador)"}
          </span>
          <button type="button" onClick={selectAllVisible} className="min-h-11 text-xs font-medium text-brand-600 hover:underline">
            Seleccionar visibles
          </button>
          {selectedIds.size > 0 && (
            <>
              <button type="button" onClick={clearSelection} className="min-h-11 text-xs font-medium text-gray-500 hover:underline">
                Limpiar selección
              </button>
              <button
                type="button"
                onClick={() => setConfirmingBulkDelete(true)}
                disabled={bulkDeleting}
                className="ml-auto min-h-11 rounded-lg bg-error-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-error-700 disabled:opacity-60"
              >
                {bulkDeleting ? "Eliminando..." : `Eliminar seleccionadas (${selectedIds.size})`}
              </button>
            </>
          )}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={institutions}
        rowKey={(inst) => inst.id}
        loading={loading}
        emptyMessage="No se encontraron instituciones educativas."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={total}
        onPageChange={setPage}
      />

      <ConfirmDialog
        isOpen={confirmingBulkDelete}
        title={`¿Eliminar permanentemente ${selectedIds.size} institución${selectedIds.size === 1 ? "" : "es"} educativa${selectedIds.size === 1 ? "" : "s"}?`}
        message="Esta acción no se puede deshacer. Las que tengan equipos CCCD o miembros TRA asociados no se eliminarán."
        confirmLabel="Eliminar"
        confirming={bulkDeleting}
        onConfirm={() => void handleBulkDelete()}
        onCancel={() => setConfirmingBulkDelete(false)}
      />
    </ModuleLayout>
  )
}
